import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Chart from "react-apexcharts";
import { motion } from "framer-motion";
import { FiTrendingUp, FiCheckCircle, FiFileText, FiShield } from "react-icons/fi";
import { toast } from "react-toastify";

import KPICard, { DashboardCard } from "../../Common/KPICard";
import SelectFilter from "../../Common/SelectFilter";
import DateRangeFilter from "../../../Common/DateFilter";
import ActionButton from "../../Common/ActionButton";
import "../../../dashboardLayout.css";
import { getClientWorkDashboardApi, getContractsApi } from "../../../Api/ClientWork/ClientWork";

const theme = {
  primary: "#0b6462",
  primaryLight: "#eefbfb",
  secondary: "#64748b",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#6366f1",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  border: "#e2e8f0",
  surface: "#ffffff",
  shadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
  shadowHover: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { y: 15, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 110 } },
};

const fmtM = (value, digits = 2) => `PKR ${(Number(value || 0) / 1000000).toFixed(digits)}M`;
const toM = (value) => Math.round((Number(value || 0) / 1000000) * 100) / 100;

const BILL_STATUS_STYLE = {
  Draft: { label: "Draft", color: "#475569", bg: "#f1f5f9" },
  Authorized: { label: "Authorized & Invoiced", color: "#0284c7", bg: "#e0f2fe" },
  "Partially Received": { label: "Partially Received", color: "#b45309", bg: "#fef3c7" },
  Paid: { label: "Paid / Received", color: "#15803d", bg: "#dcfce7" },
  Cancelled: { label: "Cancelled", color: "#b91c1c", bg: "#fee2e2" },
};
const AGING_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444"];

const ClientWorkDashboard = () => {
  const history = useHistory();
  const [selectedProject, setSelectedProject] = useState("");
  const [filterDates, setFilterDates] = useState({ from: "", to: "" });
  const [contracts, setContracts] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getContractsApi();
      if (!cancelled && result?.data?.IsSuccess === true) {
        setContracts(result.data.Data || []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reloads when the contract or the date range changes; a late answer for an old filter is dropped.
  useEffect(() => {
    let stale = false;
    (async () => {
      const result = await getClientWorkDashboardApi(
        selectedProject,
        filterDates.from,
        filterDates.to,
      );
      if (stale) {
        return;
      }
      if (result?.data?.IsSuccess === true) {
        setData(result.data.Data);
      } else {
        setData(null);
        toast.error(result?.data?.Message || "Could not load the dashboard");
      }
    })();
    return () => {
      stale = true;
    };
  }, [selectedProject, filterDates.from, filterDates.to]);

  const k = data?.kpis;
  const kpis = [
    {
      title: "Total Inward Contract Value",
      value: fmtM(k?.totalContractValue),
      subtext: `${k?.activeContracts || 0} active contract(s)`,
      subtextColor: "#64748b",
      icon: FiTrendingUp,
      color: "#0b6462",
    },
    {
      title: "Total Certified Progressive Work",
      value: fmtM(k?.certifiedGross),
      subtext: `${k?.completionPct || 0}% total completion across ${k?.activeContracts || 0} active contract(s)`,
      subtextColor: "#64748b",
      icon: FiCheckCircle,
      color: "#2563eb",
    },
    {
      title: "Total Net Invoiced & Billed (IPCs)",
      value: fmtM(k?.netInvoiced),
      subtext: `${fmtM(k?.draftWork)} in draft bills, not yet authorized`,
      subtextColor: "#64748b",
      icon: FiFileText,
      color: "#d97706",
    },
    {
      title: "Overall Retention Receivable (Holds)",
      value: fmtM(k?.retentionHeld),
      subtext: "Cumulative security deposits/holds",
      subtextColor: "#64748b",
      icon: FiShield,
      color: "#7c3aed",
    },
  ];

  const chartRows = useMemo(() => data?.chart || [], [data]);

  // Grouped Bar Chart Options (Contract Value vs. Billed vs. Collected)
  const barChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 3,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => (val >= 50 ? `PKR ${val}M` : `${val}M`),
        offsetY: -18,
        style: {
          fontSize: "10px",
          colors: ["#1e293b"],
          fontWeight: 600,
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        // Two lines per contract: the client, then its contract number
        categories: chartRows.map((row) => {
          const [name, ...rest] = row.label.split(" (");
          return rest.length ? [name, `(${rest.join(" (")}`] : name;
        }),
        labels: {
          rotate: 0,
          trim: false,
          style: {
            fontSize: "12px",
            colors: "#475569",
            fontWeight: 500,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        tickAmount: 4,
        labels: {
          formatter: (val) => `${Math.round(val)}M`,
          style: {
            fontSize: "11px",
            colors: "#64748b",
          },
        },
      },
      colors: ["#0b6462", "#2b6cb0", "#63b3ed"],
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontSize: "12px",
        markers: { radius: 3 },
      },
      grid: {
        borderColor: "#f1f5f9",
        strokeDashArray: 3,
      },
      tooltip: {
        y: {
          formatter: (val) => `PKR ${val}M`,
        },
      },
    }),
    [chartRows],
  );

  const barChartSeries = useMemo(
    () => [
      { name: "Contract Value", data: chartRows.map((row) => toM(row.contractValue)) },
      { name: "Billed", data: chartRows.map((row) => toM(row.billed)) },
      { name: "Collected", data: chartRows.map((row) => toM(row.collected)) },
    ],
    [chartRows],
  );

  // Donut Chart Options (Billing Deduction & Recoveries Analysis)
  const deductions = data?.deductions;
  const donutChartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        fontFamily: "inherit",
      },
      labels: [
        `Progressive Gross Revenue Withhold (${fmtM(deductions?.netClaim, 1)})`,
        `Mobilization Advance Recovered (${fmtM(deductions?.advanceRecovered, 1)})`,
        `Income Tax WHT & Sales Tax Deducted (${fmtM(deductions?.taxesWithheld, 1)})`,
        `Retention Money Held (${fmtM(deductions?.retentionHeld, 1)})`,
      ],
      colors: ["#0b6462", "#3182ce", "#f6ad55", "#cbd5e1"],
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`,
        style: {
          fontSize: "11px",
          fontWeight: 600,
        },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "62%",
            labels: {
              show: true,
              total: {
                show: true,
                showAlways: true,
                label: "Progressive Gross Revenue Withhold",
                fontSize: "11px",
                color: "#64748b",
                formatter: () => fmtM(deductions?.gross, 1),
              },
            },
          },
        },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val}%`,
        },
      },
    }),
    [deductions],
  );

  const share = (value) =>
    Number(deductions?.gross) > 0
      ? Math.round((Number(value || 0) / Number(deductions.gross)) * 1000) / 10
      : 0;
  const donutChartSeries = [
    share(deductions?.netClaim),
    share(deductions?.advanceRecovered),
    share(deductions?.taxesWithheld),
    share(deductions?.retentionHeld),
  ];

  // Aging receivables data: each bar is sized against the largest bracket
  const agingBuckets = data?.aging || [];
  const largestBucket = Math.max(0, ...agingBuckets.map((bucket) => Number(bucket.amount) || 0));
  const agingData = agingBuckets.map((bucket, idx) => ({
    label: bucket.label.replace(" \u2013 ", "\u2013"),
    amount: fmtM(bucket.amount, 1),
    percent: largestBucket > 0 ? Math.round((Number(bucket.amount) / largestBucket) * 100) : 0,
    color: AGING_COLORS[idx],
  }));

  // Recent Inward Client Running Bills (IPC)
  const recentBills = (data?.recentBills || []).map((bill) => {
    const style = BILL_STATUS_STYLE[bill.status] || BILL_STATUS_STYLE.Draft;
    return {
      ipcNo: bill.ipcNo,
      owner: bill.owner,
      project: bill.project,
      gross: fmtM(bill.gross),
      netBilled: fmtM(bill.netBilled),
      status: style.label,
      statusColor: style.color,
      statusBg: style.bg,
    };
  });

  const handleDateRangeChange = useCallback((range) => {
    setFilterDates(range);
  }, []);

  const handleGenerateReport = () => {
    const rangeText =
      filterDates.from && filterDates.to ? ` for ${filterDates.from} to ${filterDates.to}` : "";
    toast.info(`Generating Client Contracts Summary Report${rangeText}...`);
    history.push("/client-work/reports");
  };

  return (
    <div className="pt-2 app_font pb-5">
      {/* ── Page Heading ── */}
      {/* <div className="row">
        <div className="col-md-12 text-left page_heading">
          <h4 className="m-0">Client Contracts Dashboard</h4>
        </div>
      </div> */}

      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between my-3 app_font"
        style={{ gap: "12px" }}
      >
        <div className="d-flex flex-wrap align-items-center" style={{ gap: "12px" }}>
          <SelectFilter
            id="projectFilter"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            label=""
            defaultOptionLabel="All Active Client Contracts"
            maxWidth="260px"
            options={contracts.map((contract) => ({
              value: String(contract.Id),
              label: `${contract.ContractNo} \u2014 ${contract.ClientName} (${contract.ProjectName})`,
            }))}
          />

          <div className="d-flex align-items-center" style={{ width: "240px" }}>
            <div className="floating-field" style={{ width: "100%" }}>
              <DateRangeFilter onChange={handleDateRangeChange} />
              <label>Date Range</label>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <ActionButton
            text="Manage Clients"
            variant="info"
            onClick={() => history.push("/client-work/clients")}
          />
          <ActionButton
            text="Contracts"
            variant="info"
            onClick={() => history.push("/client-work/contracts")}
          />
          <ActionButton
            text="Running Bills"
            variant="info"
            onClick={() => history.push("/client-work/invoices")}
          />
          <ActionButton text="Generate Report" variant="save" onClick={handleGenerateReport} />
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="erp-dashboard-wrap"
      >
        {/* ── Row 1: KPI Summary Cards ── */}
        <div className="erp-kpi-grid">
          {kpis.map((kpi, idx) => (
            <KPICard
              key={idx}
              title={kpi.title}
              value={kpi.value}
              subtitle={kpi.subtext}
              icon={kpi.icon}
              themeColor={kpi.color}
              themeBgColor={`${kpi.color}15`}
              subtitleColor={kpi.subtextColor}
              itemVariants={itemVariants}
            />
          ))}
        </div>

        {/* ── Row 2: Charts (Contract Value vs. Billed & Deduction Analysis) ── */}
        <div className="erp-two-col-grid">
          {/* Left Chart */}
          <DashboardCard
            title="Contract Value vs. Billed vs. Collected"
            subtitle="Comparative breakdown across client packages"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div style={{ minHeight: "300px" }}>
              <Chart options={barChartOptions} series={barChartSeries} type="bar" height={290} />
            </div>
          </DashboardCard>

          {/* Right Chart */}
          <DashboardCard
            title="Billing Deduction & Recoveries Analysis"
            subtitle="Composition of withholdings, advances, and net claims"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div style={{ minHeight: "300px" }}>
              <Chart
                key={donutChartSeries.join("-")}
                options={donutChartOptions}
                series={donutChartSeries}
                type="donut"
                height={290}
              />
            </div>
          </DashboardCard>
        </div>

        {/* ── Row 3: Aging Receivables & Recent Bills Table ── */}
        <div className="erp-two-col-grid erp-mb-0">
          {/* Left: Net Unpaid & Aging Receivables */}
          <DashboardCard
            title="Net Unpaid & Aging Receivables"
            subtitle="Aging breakdown of outstanding certified IPC amounts"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "20px" }}
            >
              {agingData.map((item, idx) => (
                <div key={idx} className="d-flex align-items-center">
                  <div
                    style={{
                      width: "100px",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: theme.textSecondary,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    className="flex-grow-1 mx-3"
                    style={{
                      height: "26px",
                      backgroundColor: "#f1f5f9",
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${item.percent}%`,
                        backgroundColor: item.color,
                        borderRadius: "6px",
                        transition: "width 0.6s ease",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      minWidth: "90px",
                      textAlign: "right",
                      fontSize: "13px",
                      fontWeight: 800,
                      color: theme.textPrimary,
                    }}
                  >
                    {item.amount}
                  </div>
                </div>
              ))}
            </div>
          </DashboardCard>

          {/* Right: Recent Inward Client Running Bills (IPC) */}
          <DashboardCard
            title="Recent Inward Client Running Bills (IPC)"
            subtitle="Latest submitted interim payment certificates"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: "12px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", color: "#475569" }}>
                    <th style={{ fontWeight: 600, borderTop: "none" }}>IPC No.</th>
                    <th style={{ fontWeight: 600, borderTop: "none" }}>Owner Name</th>
                    <th style={{ fontWeight: 600, borderTop: "none" }}>Contract/Project</th>
                    <th style={{ fontWeight: 600, borderTop: "none" }}>Gross Value</th>
                    <th style={{ fontWeight: 600, borderTop: "none" }}>Net Billed</th>
                    <th style={{ fontWeight: 600, borderTop: "none" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBills.map((bill, idx) => (
                    <tr
                      key={idx}
                      style={{ verticalAlign: "middle", cursor: "pointer" }}
                      onClick={() => history.push("/client-work/invoices")}
                      title="Click to view Running Bills / Invoices"
                    >
                      <td style={{ fontWeight: 700, color: theme.primary }}>{bill.ipcNo}</td>
                      <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{bill.owner}</td>
                      <td style={{ color: theme.textSecondary }}>{bill.project}</td>
                      <td style={{ fontWeight: 600, color: theme.textPrimary }}>{bill.gross}</td>
                      <td style={{ fontWeight: 700, color: theme.textPrimary }}>
                        {bill.netBilled}
                      </td>
                      <td>
                        <span
                          style={{
                            backgroundColor: bill.statusBg,
                            color: bill.statusColor,
                            padding: "3px 10px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: 600,
                            display: "inline-block",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {bill.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DashboardCard>
        </div>
      </motion.div>
    </div>
  );
};

export default ClientWorkDashboard;
